import TourStepContent from '../TourStepContent';
import SandboxTourNote from '../SandboxTourNote';
import { TourConfig, TourStep } from '../tourTypes';
import {
	blockAllNoDim,
	clickThroughMask,
	onlyTargetNoDim,
	topCenterPosition,
} from '../stepStyles';
import {
	closeTicketCapture,
	ticketDrawerBack,
} from '../ticketingCaptureState';

// CANpark ticketing tour. Action-driven: the tour follows the app's real state
// (useActiveVehicle().stage) rather than Next buttons, so stages this site's
// config doesn't include are simply never reached and their steps never show.
// useTicketingTourSync maps stage -> step and detects completion on submit.
export const CANPARK_TICKET_STEP = {
	WELCOME: 0,
	SCAN: 1,
	// Camera viewfinder phase — click-through so the operator can tap to capture
	// (driven by ticketingCaptureState mode 'camera').
	CAMERA: 2,
	// In-drawer photo-upload phase — Uppy's Take Photo / Attach modal (driven by
	// useActiveVehicle().openModal). Also click-through.
	UPLOADING: 3,
	ACTION: 4,
	CAR_PHOTO: 5,
	// The "Enter details" (record form) stage is a guided tour of what you CAN do
	// on the form: an intro, then a point-and-describe pass over each control
	// (attach image, scan VIN, vehicle info, comments) with the page locked so the
	// user only reads — they aren't operating it yet. The final sub-step (SAVE)
	// unlocks so they can fill it in and submit. "Review the record" now lives on
	// the Confirm stage instead.
	TICKET_INTRO: 6,
	TICKET_ATTACH: 7,
	TICKET_VIN: 8,
	TICKET_VEHICLE_INFO: 9,
	TICKET_COMMENTS: 10,
	TICKET_SAVE: 11,
	CONFIRM: 12,
	TOW_INFO: 13,
	FINISH: 14,
	// Capture steps reached only via captureMode, appended out of flow order so the
	// earlier indices / stage ranges stay put:
	//   MANUAL_ENTRY — the bottom "Enter license plate" sheet ('manual').
	//   PREVIEW      — the post-scan "Confirm & continue" review, shown only when
	//                  the lot has "Show scan preview" on ('preview').
	MANUAL_ENTRY: 15,
	PREVIEW: 16,
} as const;

// Live drawer stage id (useActiveVehicle().stage) -> inclusive [first, last] tour
// step range. A stage can own multiple sub-steps the user pages through with
// Next; the sync hook only re-syncs when the current step is OUTSIDE the live
// stage's range (so it doesn't yank the user back mid-stage).
export const CANPARK_STAGE_RANGE: Record<string, [number, number]> = {
	action: [CANPARK_TICKET_STEP.ACTION, CANPARK_TICKET_STEP.ACTION],
	carPhoto: [CANPARK_TICKET_STEP.CAR_PHOTO, CANPARK_TICKET_STEP.CAR_PHOTO],
	ticket: [CANPARK_TICKET_STEP.TICKET_INTRO, CANPARK_TICKET_STEP.TICKET_SAVE],
	confirm: [CANPARK_TICKET_STEP.CONFIRM, CANPARK_TICKET_STEP.CONFIRM],
	towInfo: [CANPARK_TICKET_STEP.TOW_INFO, CANPARK_TICKET_STEP.TOW_INFO],
};

// The ticketing pipeline renders inside a bottom-anchored MUI Drawer. reactour
// only re-measures when an ADDED/REMOVED node itself matches an observable
// (node.matches — not its descendants), and the drawer paper is fixed-height so
// it never resizes. Data-dependent content (e.g. the action buttons, which wait
// on the loaded recommendation) mounts AFTER its step is already active, so we
// also observe each step's own anchor: when it appears, reactour re-measures and
// the popover shows. Without this the popover stays hidden if the anchor mounts
// late (the manual-entry path is fast enough to hit this; scanning isn't).
const drawerStepObs = (targetSelector: string) => ({
	mutationObservables: ['.MuiDrawer-paperAnchorBottom', targetSelector],
	resizeObservables: ['.MuiDrawer-paperAnchorBottom', targetSelector],
});

// The record form's Save/Next button (type=submit). The final "Enter details"
// step blocks the whole page and is Next-driven, so its Next programmatically
// submits the form — the app then advances to Confirm (or saves) and the sync
// hook moves the tour forward. A programmatic .click() bypasses the tour overlay.
const submitTicketForm = () => {
	const btn = document.querySelector('[data-tour="ticket-save"]');
	if (btn instanceof HTMLElement) btn.click();
};

const steps: TourStep[] = [
	{
		// Intro: don't grey out, and leave the customer/location picker clickable
		// (the un-dimmed hole) so operators can change it — everything else is
		// blocked, only Next proceeds. The bar starts collapsed (lot preselected)
		// and expands when clicked, so observe it to re-measure the hole when it
		// swaps collapsed <-> expanded.
		selector: '[data-tour="ticket-select-customer"]',
		position: 'center',
		styles: onlyTargetNoDim,
		mutationObservables: ['[data-tour="ticket-select-customer"]'],
		resizeObservables: ['[data-tour="ticket-select-customer"]'],
		content: (
			<TourStepContent
				title="Scan License Plate 🚗"
				body={
					<>
						<span>
							Your customer and location are set—change them here if
							needed, or hit Next to continue.
						</span>
						<SandboxTourNote>
							<strong>Note:</strong> This is a sandbox walkthrough. No real
							record will be created.
						</SandboxTourNote>
					</>
				}
			/>
		),
	},
	{
		selector: '[data-tour="ticket-scan-btn"]',
		position: 'top',
		content: (
			<TourStepContent
				awaitAction
				tailPosition="top"
				body="Start here — scan a license plate, or tap “Type plate instead” to enter it by hand."
			/>
		),
	},
	{
		// Camera viewfinder. Invisible + click-through mask so the operator can tap
		// to capture; NOTE: do NOT set stepInteraction:false — with selector 'body'
		// that makes reactour drop a screen-sized `highlightedArea` rect
		// (pointerEvents:auto) that blocks every click. Pinned top-centre so it
		// clears the camera below.
		selector: 'body',
		position: topCenterPosition,
		styles: clickThroughMask,
		content: (
			<TourStepContent
				awaitAction
				tailPosition="top"
				onBack={closeTicketCapture}
				body="Point the camera at the license plate and tap the middle of the frame to capture it."
			/>
		),
	},
	{
		// In-drawer photo upload (Uppy Take Photo / Attach modal). Same
		// click-through, top-centre treatment so it clears the upload dialog.
		selector: 'body',
		position: topCenterPosition,
		styles: clickThroughMask,
		content: (
			<TourStepContent
				awaitAction
				tailPosition="top"
				onBack={ticketDrawerBack}
				body="Follow the on-screen prompts to add the photo — the walkthrough continues automatically once you’re done."
			/>
		),
	},
	{
		// Non-blocking: no dim, click-through, so the operator can pick any action.
		// Anchored to the "more actions" (three-dots) button, but we observe the
		// stable ticket-action wrapper so reactour re-measures once the buttons
		// mount (the three-dots appears late, after the vehicle-history fetch).
		selector: '[data-tour="ticket-action-more"]',
		position: 'right',
		styles: clickThroughMask,
		content: (
			<TourStepContent
				awaitAction
				tailPosition="right"
				onBack={ticketDrawerBack}
				body="Decide what to do: record the vehicle, issue a warning, or start a tow. Pick an action to continue."
			/>
		),
		...drawerStepObs('[data-tour="ticket-action"]'),
	},
	{
		// No dimming / click-through: taking a photo opens the device camera and
		// Uppy's "Upload 1 file → Done" UI outside the spotlight, which a normal
		// mask would block.
		selector: '[data-tour="ticket-car-photo"]',
		position: 'top',
		styles: clickThroughMask,
		content: (
			<TourStepContent
				awaitAction
				tailPosition="top"
				onBack={ticketDrawerBack}
				body="Capture a photo of the vehicle — or skip this step."
			/>
		),
		...drawerStepObs('[data-tour="ticket-car-photo"]'),
	},
	{
		// TICKET_INTRO — record-form overview. Lock the page (only Next) but don't
		// grey it out, then walk the user through what they can do on this page.
		selector: 'body',
		position: 'center',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				onBack={ticketDrawerBack}
				title="Enter the details"
				body="This is the record form. Here you can attach images, scan the VIN, and add vehicle info and comments. Let’s look at what you can do — hit Next."
			/>
		),
	},
	{
		// TICKET_ATTACH — point at "Attach Image". Info only: page locked, no grey,
		// nothing operable but Back/Next/Skip. Just showing what's available.
		selector: '[data-tour="ticket-attach"]',
		position: 'bottom',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				tailPosition="bottom"
				body="Attach more photos of the vehicle here — as many as you need."
			/>
		),
		...drawerStepObs('[data-tour="ticket-attach"]'),
	},
	{
		// TICKET_VIN — point at "Scan VIN".
		selector: '[data-tour="ticket-vin"]',
		position: 'bottom',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				tailPosition="bottom"
				tailOffset={-80}
				body="Scan the vehicle’s VIN to pull in more detailed records."
			/>
		),
		...drawerStepObs('[data-tour="ticket-vin"]'),
	},
	{
		// TICKET_VEHICLE_INFO — point at the Vehicle Info field.
		selector: '[data-tour="ticket-vehicle-info"]',
		position: 'top',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				tailPosition="top"
				body="Optionally type details about the vehicle — make, model and colour."
			/>
		),
		...drawerStepObs('[data-tour="ticket-vehicle-info"]'),
	},
	{
		// TICKET_COMMENTS — point at the Additional Comments field.
		selector: '[data-tour="ticket-comments"]',
		position: 'top',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				tailPosition="top"
				body="Add any extra notes about the record here — this is optional too."
			/>
		),
		...drawerStepObs('[data-tour="ticket-comments"]'),
	},
	{
		// TICKET_SAVE — still info-only: keep the page fully locked (only Next), and
		// let Next submit the form for the user so the app advances to Confirm/save
		// and the sync hook carries the tour forward.
		selector: '[data-tour="ticket-save"]',
		position: 'top',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				tailPosition="top"
				tailOffset={-64}
				onNext={submitTicketForm}
				body="Now that the record is filled, click Next to continue."
			/>
		),
		...drawerStepObs('[data-tour="ticket-save"]'),
	},
	{
		// CONFIRM — "Review the record". No dim / no overlay blocking the view, so
		// the operator can look over the whole record; only Save stays clickable.
		selector: '[data-tour="ticket-confirm"]',
		position: 'top',
		styles: onlyTargetNoDim,
		content: (
			<TourStepContent
				awaitAction
				tailPosition="top"
				tailOffset={-64}
				onBack={ticketDrawerBack}
				title="Review the record"
				body="This is an overview of the record and this vehicle’s past records. Look it over, then click “Save” to complete the sandbox walkthrough."
			/>
		),
		...drawerStepObs('[data-tour="ticket-confirm"]'),
	},
	{
		selector: '[data-tour="ticket-tow-info"]',
		position: 'top',
		content: (
			<TourStepContent
				awaitAction
				tailPosition="top"
				onBack={ticketDrawerBack}
				body="Because this is a tow, here are the towing instructions to coordinate the pickup. Close when you’re done."
			/>
		),
		...drawerStepObs('[data-tour="ticket-tow-info"]'),
	},
	{
		selector: 'body',
		position: 'center',
		stepInteraction: false,
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				finalStep
				title="Nicely done! 🎉"
				body={
					<>
						<span>That’s how you create a record in CANpark.</span>
						<SandboxTourNote>
							<strong>Sandbox reminder:</strong> No real record was created
							and nothing you entered was saved.
						</SandboxTourNote>
					</>
				}
			/>
		),
	},
	{
		// Index 11 (MANUAL_ENTRY) — appended out of flow order. Points at the
		// bottom "Enter license plate" sheet; click-through so the input, the state
		// dropdown (renders in a portal) and Log all work.
		selector: '[data-tour="manual-plate-sheet"]',
		position: 'top',
		peekPlacement: 'left',
		styles: clickThroughMask,
		content: (
			<TourStepContent
				tailPosition="top"
				awaitAction
				onBack={closeTicketCapture}
				body="Type the license plate and pick the state, then tap “Log” to pull up the vehicle."
			/>
		),
		...drawerStepObs('[data-tour="manual-plate-sheet"]'),
	},
	{
		// Index 12 (PREVIEW) — the post-scan review screen ('preview' captureMode),
		// shown only when the lot keeps "Show scan preview" on. Points at the
		// Confirm & continue button; click-through so the operator can edit the
		// plate and confirm.
		selector: '[data-tour="scan-preview-confirm"]',
		position: 'top',
		styles: clickThroughMask,
		content: (
			<TourStepContent
				awaitAction
				tailPosition="top"
				onBack={closeTicketCapture}
				body="Check the plate was read correctly, then tap “Confirm & continue” — or “Retry” to scan again."
			/>
		),
	},
];

export const canparkTicketingTour: TourConfig = {
	steps,
	startRoute: '/canpark',
	allowedRoutes: ['/canpark'],
};
